/*
* Justified photo gallery (`.justified-gallery`, used by #photoGalleries on
* gallery_detail.html). Lays photos out in rows where every photo shares the
* row's height and each photo's width is derived from its own aspect ratio
* (read from a `data-aspect-ratio="width/height"` attribute set server-side),
* so a row's photos always sum -- with gaps -- to exactly the container's
* width. No cropping beyond sub-pixel rounding, since each photo is scaled
* uniformly rather than clipped to a fixed box.
*
* Row size (how many photos land in a row) is randomized -- 1, 2, or 3 below
* the per-breakpoint cap (2 below `md`, 3 at `md` and up) -- rather than
* being driven purely by target width, so the grid doesn't settle into an
* all-3-per-row pattern. The randomization is weighted by the upcoming
* photo's orientation: landscape photos are more likely to land in a
* 1-photo row (where a wide photo reads best), portrait photos are more
* likely to land in a 3-photo row. Photo order is otherwise preserved --
* rows are formed by walking the photos in sequence, not by reordering them
* by orientation.
*/
(function() {
    const MAX_PER_ROW_MOBILE = 2
    const MAX_PER_ROW_DESKTOP = 3
    const TARGET_HEIGHT_MOBILE = 260
    const TARGET_HEIGHT_DESKTOP = 340
    const DEFAULT_ASPECT_RATIO = 4 / 3
    const LANDSCAPE_MIN_RATIO = 1.2
    const PORTRAIT_MAX_RATIO = 0.83
    // Matches the $grid-breakpoints "md" value in src/css/_vars.scss.
    const desktop_query = window.matchMedia("(min-width: 768px)")

    function parse_aspect_ratio(value) {
        if (!value) {
            return DEFAULT_ASPECT_RATIO
        }
        const [width, height] = value.split("/").map(Number)
        if (!width || !height) {
            return DEFAULT_ASPECT_RATIO
        }
        return width / height
    }

    function weighted_random(weights) {
        const total = Object.values(weights).reduce((sum, w) => sum + w, 0)
        let roll = Math.random() * total
        for (const [size, weight] of Object.entries(weights)) {
            roll -= weight
            if (roll <= 0) {
                return Number(size)
            }
        }
        return Number(Object.keys(weights)[0])
    }

    // Picks how many photos the next row should take, weighted by the
    // orientation of the photo the row would start with.
    function choose_row_size(nextAspectRatio, maxPerRow) {
        let weights = { 1: 2, 2: 3, 3: 3 }
        if (nextAspectRatio >= LANDSCAPE_MIN_RATIO) {
            weights = { 1: 4, 2: 3, 3: 1 }
        } else if (nextAspectRatio <= PORTRAIT_MAX_RATIO) {
            weights = { 1: 1, 2: 3, 3: 4 }
        }
        if (maxPerRow < 3) {
            delete weights[3]
        }
        if (maxPerRow < 2) {
            delete weights[2]
        }
        return weighted_random(weights)
    }

    function group_into_rows(items, maxPerRow) {
        const rows = []
        let index = 0
        while (index < items.length) {
            const remaining = items.length - index
            const rowSize = Math.min(choose_row_size(items[index].aspectRatio, maxPerRow), remaining)
            rows.push(items.slice(index, index + rowSize))
            index += rowSize
        }
        return rows
    }

    function size_rows(rows, containerWidth, targetHeight, gap, maxPerRow) {
        rows.forEach((rowItems, index) => {
            const isLastRow = index === rows.length - 1
            const aspectRatioSum = rowItems.reduce((sum, entry) => sum + entry.aspectRatio, 0)
            const totalGap = gap * (rowItems.length - 1)

            // A short trailing row (fewer photos than the cap) stays at the
            // target height instead of being stretched edge-to-edge --
            // otherwise a leftover photo or two could end up enormous.
            const stretch = !isLastRow || rowItems.length >= maxPerRow
            const rowHeight = stretch
                ? (containerWidth - totalGap) / aspectRatioSum
                : targetHeight

            rowItems.forEach(({ item, aspectRatio }) => {
                const width = rowHeight * aspectRatio
                item.style.width = `${width}px`
                item.style.height = `${rowHeight}px`
                item.style.flex = `0 0 ${width}px`
            })
        })
    }

    function layout(container) {
        const items = Array.from(container.children).map((item) => ({
            item,
            aspectRatio: parse_aspect_ratio(item.dataset.aspectRatio),
        }))
        if (!items.length) {
            return
        }

        const containerWidth = container.clientWidth
        if (!containerWidth) {
            return
        }

        const isDesktop = desktop_query.matches
        const maxPerRow = isDesktop ? MAX_PER_ROW_DESKTOP : MAX_PER_ROW_MOBILE
        const targetHeight = isDesktop ? TARGET_HEIGHT_DESKTOP : TARGET_HEIGHT_MOBILE
        const gap = parseFloat(getComputedStyle(container).columnGap) || 0

        // Row groupings are random, so they're only (re)computed when the
        // breakpoint's max-per-row cap changes -- a plain resize just
        // re-measures the existing groups against the new container width,
        // instead of reshuffling the whole gallery on every resize tick.
        if (!container._justifiedRows || container._justifiedMaxPerRow !== maxPerRow) {
            container._justifiedRows = group_into_rows(items, maxPerRow)
            container._justifiedMaxPerRow = maxPerRow
        }

        size_rows(container._justifiedRows, containerWidth, targetHeight, gap, maxPerRow)
        container.classList.add("justified-gallery-ready")
    }

    function layout_all() {
        document.querySelectorAll(".justified-gallery").forEach(layout)
    }

    document.addEventListener("DOMContentLoaded", layout_all)

    let resize_timeout
    window.addEventListener("resize", () => {
        clearTimeout(resize_timeout)
        resize_timeout = setTimeout(layout_all, 150)
    })
})()
